import React from 'react';
import { Pause, Play } from 'lucide-react';

import { cn } from 'shadcn/lib/utils';

/**
 * DashboardHeader
 *
 * Title and subtitle for the Fulfillment Lifecycle Dashboard.
 * Accepts optional children for rendering refresh indicators or other controls
 * in the subtitle area (composition over boolean props).
 *
 * @param {Object} props
 * @param {string} props.facilityName - Name of the current facility
 * @param {string} props.selectedDate - Currently selected date (YYYY-MM-DD) for display
 * @param {React.ReactNode} [props.children] - Optional controls rendered in the subtitle row
 */
function DashboardHeader({ children, facilityName, selectedDate }) {
    const formattedDate = selectedDate
        ? new Date(`${selectedDate}T00:00:00`).toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              year: 'numeric',
          })
        : '';

    return (
        <div className="space-y-1">
            <h1 className="text-2xl font-bold tracking-tight">Shipment Status Dashboard</h1>
            <div className="flex items-center gap-2">
                <p className="text-sm text-muted-foreground">
                    {facilityName && <span>{facilityName} &bull; </span>}
                    {formattedDate && <span>{formattedDate} &bull; </span>}
                    Monitoring warehouse flow and pull time compliance.
                </p>
                {children}
            </div>
        </div>
    );
}

/**
 * AutoRefreshIndicator
 *
 * Toggle button that shows auto-refresh status and allows toggling.
 *
 * @param {Object} props
 * @param {boolean} props.enabled - Whether auto-refresh is currently active
 * @param {Function} props.onToggle - Callback to toggle auto-refresh
 */
export function AutoRefreshIndicator({ enabled, onToggle }) {
    const label = enabled ? 'Auto-refreshes every 15s' : 'Auto-refresh paused';

    return (
        <button
            type="button"
            onClick={onToggle}
            className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-colors',
                enabled
                    ? 'bg-green-50 text-green-700 hover:bg-green-100'
                    : 'bg-amber-50 text-amber-700 hover:bg-amber-100',
            )}
        >
            {enabled ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
            {label}
        </button>
    );
}

/**
 * AutoRefreshLabel
 *
 * Read-only label that displays auto-refresh status without a toggle control.
 *
 * @param {Object} props
 * @param {boolean} props.enabled - Whether auto-refresh is currently active
 */
export function AutoRefreshLabel({ enabled }) {
    const label = enabled ? 'Auto-refreshes every 15s' : 'Auto-refresh paused';
    return <span className="text-xs text-muted-foreground">{label}</span>;
}

export default DashboardHeader;
